"use client";

import React from "react";
import { Icon } from "@iconify/react";
import { Brand, DashboardConfig } from "@webuild-ai/react-auth";

import { DashboardLayout } from "./DashboardLayout";

interface MicrofrontendDashboardProps {
  /** Dashboard configuration with Module Federation settings */
  config: DashboardConfig;
  /** Current brand configuration */
  brand: Brand;
}

/**
 * Microfrontend dashboard component using Module Federation.
 *
 * This is a placeholder component that will be enhanced once Module Federation
 * is configured in vite.config.ts using @originjs/vite-plugin-federation.
 *
 * Future implementation will:
 * 1. Dynamically load remoteEntry.js from config.remoteEntry or config.url
 * 2. Import the exposed module from config.exposedModule
 * 3. Render the remote React component with shared context (brand, theme, user)
 *
 * Benefits over iframe:
 * - Shared dependencies (React, HeroUI) - smaller bundle
 * - Direct prop passing for brand/theme context
 * - Better error boundaries and debugging
 * - Consistent styling with host application
 */
export const MicrofrontendDashboard: React.FC<MicrofrontendDashboardProps> = ({
  config,
  brand,
}) => {
  // Future: Dynamic import using Module Federation
  // const RemoteComponent = useMicrofrontend({
  //   remoteName: config.remoteName,
  //   remoteEntry: config.remoteEntry || config.url,
  //   exposedModule: config.exposedModule,
  // });

  return (
    <DashboardLayout brand={brand} subtitle={config.subtitle} title={config.title}>
      <div className="flex flex-col items-center justify-center h-full gap-6 text-center">
        <div className="p-4 rounded-full bg-primary/10">
          <Icon className="text-primary" icon="solar:code-square-outline" width={64} />
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-semibold">Microfrontend Dashboard</h2>
          <p className="text-default-500 max-w-md">
            This dashboard type uses Module Federation to load a remote React application. Configure
            vite-plugin-federation to enable this feature.
          </p>
        </div>

        <div className="bg-default-100 rounded-lg p-4 text-left text-sm font-mono max-w-lg w-full">
          <p className="text-default-600 mb-2">Configuration:</p>
          <div className="space-y-1 text-default-500">
            {config.remoteName && (
              <p>
                <span className="text-primary">remoteName:</span> {config.remoteName}
              </p>
            )}
            {(config.remoteEntry || config.url) && (
              <p>
                <span className="text-primary">remoteEntry:</span>{" "}
                {config.remoteEntry || config.url}
              </p>
            )}
            {config.exposedModule && (
              <p>
                <span className="text-primary">exposedModule:</span> {config.exposedModule}
              </p>
            )}
          </div>
        </div>

        <p className="text-xs text-default-400">
          See{" "}
          <a
            className="text-primary hover:underline"
            href="https://github.com/AlemuBelaworky/vite-plugin-federation"
            rel="noopener noreferrer"
            target="_blank"
          >
            @originjs/vite-plugin-federation
          </a>{" "}
          for setup instructions.
        </p>
      </div>
    </DashboardLayout>
  );
};
